import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableFooter, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { FixedAssetReportService } from '../../services/fixedAssetReport.service';
import { AssetRegisterReport as AssetRegisterReportData } from '../../interfaces/fixedAsset/fixedAssetReport.interface';
import { FIXED_ASSET_STATUS_COLORS } from '../../interfaces/fixedAsset/fixedAsset.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { humanizeEnum } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { downloadCsv } from '../../utils/csv';

const money = (n: number) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = () => toLocalIsoDate();

const AssetRegisterReport: React.FC = () => {
  const toast = useToast();
  const [asOf, setAsOf] = useState(today());
  const [report, setReport] = useState<AssetRegisterReportData | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const data = await FixedAssetReportService.register(institutionId!, asOf);
      setReport(data);
    } catch (e) {
      toast.showApiError(e, 'Failed to load asset register');
    } finally {
      setLoading(false);
    }
  };

  // Open with the figures showing, using today's date.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const handleExportCsv = () => {
    if (!report) return;
    const headers = ['Code', 'Name', 'Category', 'Acquired', 'Cost', 'Accumulated', 'NBV', 'Status'];
    const dataRows = report.rows.map(r => [
      r.assetCode, r.name, r.category ?? '', r.acquisitionDate,
      r.cost, r.accumulatedDepreciation, r.netBookValue, r.status,
    ]);
    const totalsRow = [
      'TOTALS', '', '', '',
      report.totalCost, report.totalAccumulatedDepreciation, report.totalNetBookValue, '',
    ];
    downloadCsv(`asset-register-${asOf}.csv`, headers, [...dataRows, totalsRow]);
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="fixedAssets.reg.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }} data-help-section="fixedAssets.reg.filter">
          <Typography variant="h5" sx={{ flex: { xs: '1 0 100%', sm: '0 0 auto' } }}>Asset Register</Typography>
          <TextField
            data-help="fixedAssets.reg.asOf"
            label="As of"
            type="date"
            value={asOf}
            onChange={e => setAsOf(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ flex: '1 1 170px', maxWidth: 220 }}
          />
          <Button data-help="fixedAssets.reg.run" variant="contained" onClick={load} disabled={loading}>Run</Button>
        </Box>
        <Button
          data-help="fixedAssets.reg.export"
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportCsv}
          disabled={!report || loading}
        >
          Export CSV
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : report && (
        <TableContainer component={Paper}>
          <Table data-help-section="fixedAssets.reg.table">
            <TableHead>
              <TableRow>
                <TableCell data-help="fixedAssets.reg.col.code">Code</TableCell>
                <TableCell data-help="fixedAssets.reg.col.name">Name</TableCell>
                <TableCell data-help="fixedAssets.reg.col.category">Category</TableCell>
                <TableCell data-help="fixedAssets.reg.col.acquired">Acquired</TableCell>
                <TableCell data-help="fixedAssets.reg.col.cost" align="right">Cost</TableCell>
                <TableCell data-help="fixedAssets.reg.col.accum" align="right">Accumulated</TableCell>
                <TableCell data-help="fixedAssets.reg.col.nbv" align="right">NBV</TableCell>
                <TableCell data-help="fixedAssets.reg.col.status">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No assets as of this date.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {report.rows.map(row => (
                <TableRow key={row.fixedAssetId} hover>
                  <TableCell>{row.assetCode}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.category ?? '—'}</TableCell>
                  <TableCell>{formatDate(row.acquisitionDate)}</TableCell>
                  <TableCell align="right">{money(row.cost)}</TableCell>
                  <TableCell align="right">{money(row.accumulatedDepreciation)}</TableCell>
                  <TableCell align="right">{money(row.netBookValue)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={humanizeEnum(row.status)} color={FIXED_ASSET_STATUS_COLORS[row.status]} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                <TableCell colSpan={4}>TOTALS</TableCell>
                <TableCell align="right">{money(report.totalCost)}</TableCell>
                <TableCell align="right">{money(report.totalAccumulatedDepreciation)}</TableCell>
                <TableCell align="right">{money(report.totalNetBookValue)}</TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AssetRegisterReport;
